import React, { useCallback } from 'react';
import styled from '@emotion/styled';

import { BadgeList } from '../components/Badge/BadgeList';
import { PromotionLayout } from '../components/PromotionLayout';
import { useProductMetadata } from '../hooks/useProductMetadata';
import { usePromotionTimerService } from '../services/usePromotionTimerService';
import { CouponContainer } from './CouponContainer';
import { CreatorCouponContainer } from './CreatorCouponContainer';
import { PriceContainer } from './PriceContainer';

export interface ProductMetadataContainerProps {}

export const ProductMetadataContainer: React.FC<React.PropsWithChildren<ProductMetadataContainerProps>> = () => {
  const {
    below_atf_price_badge_list,
    floatBannerInfo,
    displayPriceLine,
    hasBadge,
    shouldShowPromotionLayout,
    impressionPromotionBannerRef,
    promotionBannerVisibilityRef,
    handleHeaderClick,
  } = useProductMetadata();

  const { getFormattedCountdown, shouldShowTimerIcon } = usePromotionTimerService({
    timestamp: floatBannerInfo?.end_time ?? (floatBannerInfo ? null : 0),
    onExpire: undefined,
  });

  // 두 ref를 병합 (impression 로깅 + visibility 추적)
  const mergedRef = useCallback(
    (node: HTMLDivElement | null) => {
      impressionPromotionBannerRef(node);
      promotionBannerVisibilityRef(node);
    },
    [impressionPromotionBannerRef, promotionBannerVisibilityRef],
  );

  const content = (
    <Container displayPriceLine={displayPriceLine}>
      <div>
        <PriceContainer />
        {hasBadge && <BadgeList badgeList={below_atf_price_badge_list} />}
      </div>
      <MetaDataCoupon className='metadata-coupon'>
        <CouponContainer />
      </MetaDataCoupon>

      <CreatorCouponContainer />
    </Container>
  );

  if (!shouldShowPromotionLayout) {
    return <DefaultLayout>{content}</DefaultLayout>;
  }

  return (
    <PromotionLayout
      ref={mergedRef}
      bannerInfo={floatBannerInfo}
      countdown={getFormattedCountdown()}
      showTimerIcon={shouldShowTimerIcon()}
      onHeaderClick={handleHeaderClick}
    >
      {content}
    </PromotionLayout>
  );
};

const DefaultLayout = styled.div`
  padding: 16px;
`;

const Container = styled.div<{ displayPriceLine: number }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  ${({ displayPriceLine }) => {
    if (displayPriceLine === 3) {
      return `
        .metadata-coupon {
          padding: 10px 0 0 0;
        }
      `;
    }
  }}
`;

const MetaDataCoupon = styled.div`
  margin: 0 0 0 4px;
`;
